<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="news-page">
    <!-- HERO -->
    <section class="page-hero">
      <div class="page-hero-inner">
        <div class="section-label">Latest News</div>
        <h1 class="page-title">最新消息</h1>
        <p class="page-sub">掌握綠憩植研的最新動態、新品資訊與活動消息</p>
      </div>
    </section>

    <!-- NEWS -->
    <section class="news-section">
      <div class="news-inner">
        <!-- 分類篩選 -->
        <div class="news-filter">
          <button
            :class="['filter-btn', { active: activeCategory === '全部' }]"
            @click="activeCategory = '全部'"
          >
            全部
          </button>
          <button
            v-for="cat in categories"
            :key="cat.id"
            :class="['filter-btn', { active: activeCategory === cat.name }]"
            @click="activeCategory = cat.name"
          >
            {{ cat.name }}
          </button>
        </div>

        <!-- 消息列表 -->
        <div v-if="loading" class="loading-wrap">
          <div class="loading-spinner"></div>
          <p>載入消息中...</p>
        </div>
        <div v-else-if="filteredNews.length > 0" class="news-grid">
          <div
            v-for="n in filteredNews"
            :key="n.id"
            class="news-card"
            @click="openPreview(n)"
          >
            <div class="news-img">
              <img
                v-if="n.imageUrl"
                :src="n.imageUrl"
                :alt="n.title"
                class="news-image"
              />
              <span v-else class="news-emoji">{{ n.emoji }}</span>
              <span class="news-cat-badge">{{ n.category }}</span>
            </div>
            <div class="news-body">
              <div class="news-date">{{ formatDate(n.date) }}</div>
              <div class="news-title">{{ n.title }}</div>
              <div class="news-excerpt">{{ n.excerpt }}</div>
            </div>
          </div>
        </div>
        <div v-else class="no-data">
          <p>暫無消息資料</p>
        </div>
      </div>
    </section>

    <!-- 文章預覽 Modal -->
    <div
      v-if="previewArticle"
      class="preview-overlay"
      @click.self="closePreview"
    >
      <div class="preview-modal">
        <button class="preview-close" @click="closePreview">✕</button>

        <div class="preview-img-wrap">
          <img
            v-if="previewArticle.imageUrl"
            :src="previewArticle.imageUrl"
            :alt="previewArticle.title"
            class="preview-image"
          />
          <span v-else class="preview-emoji">{{ previewArticle.emoji }}</span>
        </div>

        <div class="preview-body">
          <span class="preview-cat-badge">{{ previewArticle.category }}</span>
          <div class="preview-date">{{ formatDate(previewArticle.date) }}</div>
          <h2 class="preview-title">{{ previewArticle.title }}</h2>
          <p class="preview-excerpt">{{ previewArticle.excerpt }}</p>
        </div>
      </div>
    </div>

    <!-- CTA -->
    <section class="news-cta">
      <div class="cta-inner">
        <h2>想第一手獲得最新消息？</h2>
        <p>聯絡我們，掌握最新植栽資訊與活動優惠</p>
        <router-link to="/contact" class="btn-primary">聯絡我們</router-link>
      </div>
    </section>

    <!-- FOOTER -->
    <footer class="footer">
      <div class="footer-inner">
        <p>© 2026 綠憩植研 Green Rich Geam. All rights reserved.</p>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import axios from "axios";

const activeCategory = ref("全部");
const categories = ref([]);
const newsItems = ref([]);
const loading = ref(true);
const previewArticle = ref(null);

const loadCategories = async () => {
  try {
    const res = await axios.get("/newscategories");
    categories.value = res.data;
  } catch (error) {
    console.error("載入分類失敗:", error);
  }
};

const loadNews = async () => {
  try {
    const res = await axios.get("/news");
    newsItems.value = res.data;
  } catch (error) {
    console.error("載入消息失敗:", error);
  } finally {
    loading.value = false;
  }
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return `${d.getFullYear()} 年 ${d.getMonth() + 1} 月 ${d.getDate()} 日`;
};

const filteredNews = computed(() => {
  if (activeCategory.value === "全部") return newsItems.value;
  return newsItems.value.filter((n) => n.category === activeCategory.value);
});

const openPreview = (article) => {
  previewArticle.value = article;
  document.body.style.overflow = "hidden";
};

const closePreview = () => {
  previewArticle.value = null;
  document.body.style.overflow = "";
};

onMounted(() => {
  loadCategories();
  loadNews();
});
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.news-page {
  font-family: "Noto Sans TC", "Microsoft JhengHei", sans-serif;
  background: #f7f5ef;
  color: #1c2b1a;
}

.page-hero {
  background: linear-gradient(135deg, #2d5016 0%, #4e7b4f 100%);
  color: white;
  padding: 80px 40px;
  text-align: center;
}

.page-hero-inner {
  max-width: 600px;
  margin: 0 auto;
}

.section-label {
  font-size: 12px;
  letter-spacing: 3px;
  text-transform: uppercase;
  opacity: 0.7;
  margin-bottom: 16px;
}

.page-title {
  font-size: 3em;
  font-weight: 600;
  margin-bottom: 16px;
}

.page-sub {
  font-size: 1.1em;
  opacity: 0.85;
  line-height: 1.7;
}

.news-section {
  max-width: 1200px;
  margin: 80px auto;
  padding: 0 40px;
}

.news-inner {
  width: 100%;
}

.news-filter {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 40px;
}

.filter-btn {
  padding: 8px 20px;
  background: white;
  color: #6b7f68;
  border: 1.5px solid #ddd;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  font-family: inherit;
}

.filter-btn:hover {
  border-color: #4e7b4f;
  color: #4e7b4f;
}

.filter-btn.active {
  background: #4e7b4f;
  color: white;
  border-color: #4e7b4f;
}

.news-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 30px;
}

.news-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s;
  cursor: pointer;
}

.news-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
}

.news-img {
  height: 180px;
  background: linear-gradient(135deg, #eaf2e0 0%, #c8ddb0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.news-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.news-emoji {
  font-size: 60px;
  opacity: 0.8;
}

.news-cat-badge {
  position: absolute;
  top: 16px;
  left: 16px;
  background: #4e7b4f;
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.news-body {
  padding: 24px;
}

.news-date {
  font-size: 12px;
  color: #9baa97;
  margin-bottom: 10px;
}

.news-title {
  font-size: 16px;
  font-weight: 600;
  color: #1c2b1a;
  margin-bottom: 12px;
  line-height: 1.5;
}

.news-excerpt {
  font-size: 14px;
  color: #6b7f68;
  line-height: 1.7;
}

.loading-wrap {
  text-align: center;
  padding: 80px 20px;
  color: #6b7f68;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #eaf2e0;
  border-top: 3px solid #4e7b4f;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.no-data {
  text-align: center;
  padding: 80px 20px;
  color: #9baa97;
  font-size: 18px;
}

/* 文章預覽 Modal */
.preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.preview-modal {
  background: white;
  border-radius: 20px;
  max-width: 720px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.preview-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  font-size: 16px;
  cursor: pointer;
  z-index: 2;
  color: #1c2b1a;
  transition: all 0.2s;
}

.preview-close:hover {
  background: white;
  transform: scale(1.1);
}

.preview-img-wrap {
  width: 100%;
  background: linear-gradient(135deg, #eaf2e0 0%, #c8ddb0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  max-height: 420px;
  border-radius: 20px 20px 0 0;
  overflow: hidden;
}

.preview-image {
  width: 100%;
  height: 100%;
  max-height: 420px;
  object-fit: contain;
}

.preview-emoji {
  font-size: 100px;
  padding: 60px 0;
}

.preview-body {
  padding: 32px;
}

.preview-cat-badge {
  display: inline-block;
  background: #4e7b4f;
  color: white;
  padding: 4px 14px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 12px;
}

.preview-date {
  font-size: 13px;
  color: #9baa97;
  margin-bottom: 12px;
}

.preview-title {
  font-size: 24px;
  font-weight: 600;
  color: #1c2b1a;
  line-height: 1.5;
  margin-bottom: 20px;
}

.preview-excerpt {
  font-size: 15px;
  color: #3d4f3a;
  line-height: 1.9;
  white-space: pre-wrap;
}

.news-cta {
  background: #eaf2e0;
  padding: 80px 40px;
  text-align: center;
  margin-top: 40px;
}

.cta-inner {
  max-width: 500px;
  margin: 0 auto;
}

.cta-inner h2 {
  font-size: 2em;
  color: #1c2b1a;
  margin-bottom: 12px;
}

.cta-inner p {
  color: #6b7f68;
  margin-bottom: 30px;
  font-size: 16px;
  line-height: 1.7;
}

.btn-primary {
  padding: 14px 40px;
  background: #4e7b4f;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s;
  display: inline-block;
  font-weight: 600;
}

.btn-primary:hover {
  background: #2d5016;
  transform: translateY(-2px);
}

.footer {
  background: #1c2b1a;
  color: #9baa97;
  text-align: center;
  padding: 40px;
}

.footer-inner {
  max-width: 1200px;
  margin: 0 auto;
}

@media (max-width: 768px) {
  .page-title {
    font-size: 2em;
  }

  .news-section {
    margin: 40px auto;
    padding: 0 20px;
  }

  .news-grid {
    grid-template-columns: 1fr;
  }

  .preview-body {
    padding: 24px 20px;
  }

  .preview-title {
    font-size: 20px;
  }
}
</style>
